export type MapLabelCandidate = {
  id: string
  point: [number, number]
  bounds: { x: number; y: number; width: number; height: number }
  baseFontSize: number
  priority: number
  allowOffset?: boolean
  containsPoint?: (point: [number, number]) => boolean
}

export type MapLabelPlacement = { fontSize: number; offsetX: number; offsetY: number }

type PlacedLabel = { candidate: MapLabelCandidate; placement: MapLabelPlacement }

const LABEL_GAP_PX = 4
const POINT_OFFSET_PX = 32
const MAX_FONT_GROWTH_PX = 4
const COUNTRY_NUDGE_PX = 8
const COUNTRY_OFFSETS = Array.from({ length: 9 }, (_, x) => x - 4)
  .flatMap((x) =>
    [-2, -1, 0, 1, 2].map((y) => ({ offsetX: x * COUNTRY_NUDGE_PX, offsetY: y * COUNTRY_NUDGE_PX }))
  )
  .sort(
    (a, b) =>
      a.offsetX ** 2 + a.offsetY ** 2 - (b.offsetX ** 2 + b.offsetY ** 2) ||
      Math.abs(a.offsetY) - Math.abs(b.offsetY)
  )
const CONTAINMENT_X = [0.05, 0.25, 0.5, 0.75, 0.95]
const CONTAINMENT_Y = [0.2, 0.5, 0.8]

const getScreenBounds = ({ candidate, placement }: PlacedLabel, zoom: number) => {
  const scale = placement.fontSize / candidate.baseFontSize
  const { x, y, width, height } = candidate.bounds

  return {
    left: candidate.point[0] * zoom + x * scale + placement.offsetX,
    right: candidate.point[0] * zoom + (x + width) * scale + placement.offsetX,
    top: candidate.point[1] * zoom + y * scale + placement.offsetY,
    bottom: candidate.point[1] * zoom + (y + height) * scale + placement.offsetY,
  }
}

const fitsCountry = (label: PlacedLabel, zoom: number) => {
  const { containsPoint } = label.candidate
  if (!containsPoint) return true

  const bounds = getScreenBounds(label, zoom)
  return CONTAINMENT_X.every((x) =>
    CONTAINMENT_Y.every((y) =>
      containsPoint([
        (bounds.left + (bounds.right - bounds.left) * x) / zoom,
        (bounds.top + (bounds.bottom - bounds.top) * y) / zoom,
      ])
    )
  )
}

const labelsOverlap = (first: PlacedLabel, second: PlacedLabel, zoom: number) => {
  const a = getScreenBounds(first, zoom)
  const b = getScreenBounds(second, zoom)

  return (
    a.left < b.right + LABEL_GAP_PX &&
    a.right + LABEL_GAP_PX > b.left &&
    a.top < b.bottom + LABEL_GAP_PX &&
    a.bottom + LABEL_GAP_PX > b.top
  )
}

const availableOffsets = (candidate: MapLabelCandidate, current?: MapLabelPlacement) => {
  const offsets = candidate.allowOffset
    ? [0, -POINT_OFFSET_PX, POINT_OFFSET_PX].map((offsetY) => ({ offsetX: 0, offsetY }))
    : candidate.containsPoint
      ? COUNTRY_OFFSETS
      : [{ offsetX: 0, offsetY: 0 }]
  if (!current) return offsets
  return [
    current,
    ...offsets.filter(
      ({ offsetX, offsetY }) => offsetX !== current.offsetX || offsetY !== current.offsetY
    ),
  ]
}

const findPlacement = (
  candidate: MapLabelCandidate,
  fontSize: number,
  zoom: number,
  placed: PlacedLabel[],
  current?: MapLabelPlacement
): MapLabelPlacement | undefined => {
  for (const { offsetX, offsetY } of availableOffsets(candidate, current)) {
    const placement = { fontSize, offsetX, offsetY }
    const next = { candidate, placement }
    if (
      fitsCountry(next, zoom) &&
      placed.every((existing) => !labelsOverlap(next, existing, zoom))
    ) {
      return placement
    }
  }
  return undefined
}

export const layoutMapLabels = (
  candidates: MapLabelCandidate[],
  zoom: number
): Record<string, MapLabelPlacement> => {
  const placed: PlacedLabel[] = []
  const ordered = [...candidates].sort(
    (first, second) => second.priority - first.priority || first.id.localeCompare(second.id)
  )

  for (const candidate of ordered) {
    const placement = findPlacement(candidate, candidate.baseFontSize, zoom, placed)
    if (placement) placed.push({ candidate, placement })
  }

  const fontGrowth = Math.min(MAX_FONT_GROWTH_PX, Math.max(0, Math.floor((zoom - 1) * 2)))
  for (let step = 0; step < fontGrowth; step += 1) {
    for (const label of placed) {
      const placement = findPlacement(
        label.candidate,
        label.placement.fontSize + 1,
        zoom,
        placed.filter((other) => other !== label),
        label.placement
      )
      if (placement) label.placement = placement
    }
  }

  return Object.fromEntries(placed.map(({ candidate, placement }) => [candidate.id, placement]))
}
